import { telecharger as telechargerFichier } from '../../lib/telecharger'
// Le geste de partage — deux chemins, et la règle qui gouverne chacun.
//
// L'IMAGE (décision du 22/09) : elle se suffit à elle-même, ne donne accès à rien, et son
// cartouche (source, millésime, signature) voyage avec elle. Sur ce chemin, on ne passe NI
// `url` à `navigator.share`, NI adresse dans le titre : ce serait un lien déguisé en légende.
// Les tests de ce module le vérifient toujours.
//
// LE LIEN (décision de Marc du 03/10, CDC E2 « partager un graphique par lien ») : la règle
// « aucun lien ne circule » (décision 12 du 21/09, décision I du 15/07) est levée POUR CE SEUL
// USAGE — un lien `/p/<jeton>` vers un INSTANTANÉ figé d'un graphique, fabriqué par le serveur,
// valable 14 jours, sans aucune route de l'observatoire derrière (`app/observatoire/partages.py`).
// `partagerLien` est donc le SEUL endroit qui passe une adresse à la feuille de partage, et
// seulement une adresse de partage (`/p/`) : toute autre adresse est refusée.

/** Ce qui s'est réellement passé — l'appelant s'en sert pour le retour à l'utilisateur. */
export type IssuePartage = 'natif' | 'telechargement' | 'annule'

type NavigateurPartage = Navigator & {
  canShare?: (donnees?: ShareData) => boolean
  share?: (donnees?: ShareData) => Promise<void>
}

/**
 * Le partage natif n'accepte les fichiers que sur une partie des navigateurs. `canShare`
 * doit être interrogé AVEC le fichier : desktop Safari et plusieurs navigateurs Android
 * exposent `share` mais refusent les pièces jointes. Sans cette vérification, le bouton
 * ne ferait rien du tout sur ces postes.
 */
function peutPartagerLeFichier(fichier: File): boolean {
  const nav = navigator as NavigateurPartage
  if (typeof nav.canShare !== 'function' || typeof nav.share !== 'function') return false
  try {
    return nav.canShare({ files: [fichier] })
  } catch {
    return false
  }
}

/** Repli de bureau : le PNG tombe dans les téléchargements, l'utilisateur l'attache lui-même.
 *  C'est aussi le geste du SVG (30/09) : un fichier vectoriel se pose dans un rapport ou une
 *  présentation, pas dans une messagerie — la plupart n'en affichent même pas l'aperçu. */
export function telecharger(image: Blob, nomFichier: string): void {
  telechargerFichier(image, nomFichier)
}

/**
 * Partage l'image : feuille native avec le fichier quand le poste sait le faire, sinon
 * téléchargement. Ne jette pas — un bouton de confort ne casse pas la page.
 *
 * `titre` sert d'intitulé à la feuille de partage. Il est recopié tel quel : y ajouter
 * quoi que ce soit serait le chemin par lequel une adresse finirait par revenir.
 */
export async function partagerImage(image: Blob, nomFichier: string,
                                    titre: string): Promise<IssuePartage> {
  let fichier: File | null = null
  try {
    fichier = new File([image], nomFichier, { type: image.type || 'image/png' })
  } catch {
    fichier = null   // very old Safari : constructeur File absent
  }

  if (fichier && peutPartagerLeFichier(fichier)) {
    try {
      await (navigator as NavigateurPartage).share!({ files: [fichier], title: titre })
      return 'natif'
    } catch (e) {
      // `AbortError` = l'utilisateur a fermé la feuille. Retomber sur un téléchargement
      // déposerait sur son disque le fichier qu'il vient justement de refuser.
      if (e instanceof Error && e.name === 'AbortError') return 'annule'
      // Toute autre panne (permission, quota, partage indisponible en arrière-plan) :
      // on ne laisse pas le geste sans effet, le PNG part en téléchargement.
    }
  }

  telecharger(image, nomFichier)
  return 'telechargement'
}

/** L'issue du partage d'un lien : feuille native, copie dans le presse-papiers, ou rien. */
export type IssueLien = 'natif' | 'copie' | 'annule' | 'echec'

/** Une adresse de partage, et rien d'autre : `https://<domaine>/p/<jeton de 22 caractères>`. */
export function estLienDePartage(url: string): boolean {
  try {
    const u = new URL(url)
    return (u.protocol === 'https:' || u.protocol === 'http:')
      && /^\/p\/[A-Za-z0-9_-]{22}$/.test(u.pathname) && !u.search && !u.hash
  } catch {
    return false
  }
}

/** Copie dans le presse-papiers ; `false` si le navigateur refuse (contexte non sûr…). */
export async function copierLien(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
      return true
    }
  } catch { /* refusé : l'appelant laisse le lien affiché, sélectionnable */ }
  return false
}

/**
 * Partage le LIEN d'un graphique (03/10) : feuille native avec l'adresse quand le poste sait
 * le faire (téléphone), sinon copie dans le presse-papiers. N'accepte qu'une adresse de
 * partage `/p/<jeton>` — jamais une adresse de l'observatoire.
 */
export async function partagerLien(url: string, titre: string): Promise<IssueLien> {
  if (!estLienDePartage(url)) return 'echec'
  const nav = navigator as NavigateurPartage
  const donnees: ShareData = { url, title: titre }
  let natifPossible = typeof nav.share === 'function'
  if (natifPossible && typeof nav.canShare === 'function') {
    try { natifPossible = nav.canShare(donnees) } catch { natifPossible = false }
  }
  if (natifPossible) {
    try {
      await nav.share!(donnees)
      return 'natif'
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return 'annule'
    }
  }
  return (await copierLien(url)) ? 'copie' : 'echec'
}
